/**
 * useConversationState - State machine for checkpoint conversations
 *
 * Replaces 10+ useState calls with a single, predictable state machine.
 * Makes state transitions explicit and easier to debug/test.
 */
import { useReducer, useCallback } from "react"
import { CONVERSATION_STATES, isEndedStatus } from "./constants"

// Initial state shape
const createInitialState = (initialConversation) => ({
    // Machine state
    status: initialConversation ? CONVERSATION_STATES.LOADING : CONVERSATION_STATES.IDLE,

    // Conversation data
    conversation: initialConversation,
    displayMessage: null,
    responseCount: initialConversation?.student_responses_count || 0,

    // UI state
    feedbackValue: null,
    isPetalEarned: false,
    isAnimationComplete: false,

    // Text input state (for text/text_homework responses)
    textInput: { index: null, value: "" }
})

// Action types
const ACTIONS = {
    INIT: "INIT",
    LOADED: "LOADED",
    ANIMATION_COMPLETE: "ANIMATION_COMPLETE",
    RESPONSE_SUBMITTED: "RESPONSE_SUBMITTED",
    RESPONSE_RECEIVED: "RESPONSE_RECEIVED",
    FEEDBACK_GIVEN: "FEEDBACK_GIVEN",
    PETAL_EARNED: "PETAL_EARNED",
    TEXT_INPUT_CHANGED: "TEXT_INPUT_CHANGED",
    TEXT_INPUT_CLEARED: "TEXT_INPUT_CLEARED",
    ERROR: "ERROR",
    RESET: "RESET"
}

// Reducer function
function conversationReducer(state, action) {
    switch (action.type) {
        case ACTIONS.INIT:
            return {
                ...createInitialState(action.payload),
                status: CONVERSATION_STATES.LOADING
            }

        case ACTIONS.LOADED:
            return {
                ...state,
                status: CONVERSATION_STATES.DISPLAYING,
                displayMessage: action.payload,
                isAnimationComplete: false
            }

        case ACTIONS.ANIMATION_COMPLETE:
            return {
                ...state,
                isAnimationComplete: true
            }

        case ACTIONS.RESPONSE_SUBMITTED:
            return {
                ...state,
                status: CONVERSATION_STATES.RESPONDING,
                isAnimationComplete: false
            }

        case ACTIONS.RESPONSE_RECEIVED: {
            const { conversation, agentMessage, responseCount } = action.payload
            const conversationStatus = conversation?.status
            const newStatus = isEndedStatus(conversationStatus)
                ? CONVERSATION_STATES.ENDED
                : CONVERSATION_STATES.DISPLAYING

            return {
                ...state,
                status: newStatus,
                // Update conversation data properly (merge with new agent_message)
                conversation: {
                    ...state.conversation,
                    conversation: conversation,
                    agent_message: agentMessage
                },
                displayMessage: agentMessage,
                responseCount: responseCount ?? state.responseCount + 1,
                isAnimationComplete: false
            }
        }

        case ACTIONS.FEEDBACK_GIVEN:
            return {
                ...state,
                feedbackValue: action.payload
            }

        case ACTIONS.PETAL_EARNED:
            return {
                ...state,
                isPetalEarned: true
            }

        case ACTIONS.TEXT_INPUT_CHANGED:
            return {
                ...state,
                textInput: action.payload
            }

        case ACTIONS.TEXT_INPUT_CLEARED:
            return {
                ...state,
                textInput: { index: null, value: "" }
            }

        case ACTIONS.ERROR:
            return {
                ...state,
                status: CONVERSATION_STATES.ERROR
            }

        case ACTIONS.RESET:
            return createInitialState(null)

        default:
            return state
    }
}

/**
 * Custom hook for managing conversation state
 * @param {Object} initialConversation - Initial conversation data from API
 * @returns {Object} { state, actions }
 */
export const useConversationState = (initialConversation) => {
    const [state, dispatch] = useReducer(
        conversationReducer,
        initialConversation,
        createInitialState
    )

    // Memoized action creators for cleaner component code
    const actions = {
        init: useCallback((conversation) => {
            dispatch({ type: ACTIONS.INIT, payload: conversation })
        }, []),

        loaded: useCallback((displayMessage) => {
            dispatch({ type: ACTIONS.LOADED, payload: displayMessage })
        }, []),

        animationComplete: useCallback(() => {
            dispatch({ type: ACTIONS.ANIMATION_COMPLETE })
        }, []),

        submitResponse: useCallback(() => {
            dispatch({ type: ACTIONS.RESPONSE_SUBMITTED })
        }, []),

        receiveResponse: useCallback((data) => {
            dispatch({
                type: ACTIONS.RESPONSE_RECEIVED,
                payload: {
                    conversation: data?.conversation,
                    agentMessage: data?.agent_message,
                    responseCount: data?.student_responses_count
                }
            })
        }, []),

        giveFeedback: useCallback((value) => {
            dispatch({ type: ACTIONS.FEEDBACK_GIVEN, payload: value })
        }, []),

        earnPetal: useCallback(() => {
            dispatch({ type: ACTIONS.PETAL_EARNED })
        }, []),

        setTextInput: useCallback((input) => {
            dispatch({ type: ACTIONS.TEXT_INPUT_CHANGED, payload: input })
        }, []),

        clearTextInput: useCallback(() => {
            dispatch({ type: ACTIONS.TEXT_INPUT_CLEARED })
        }, []),

        setError: useCallback(() => {
            dispatch({ type: ACTIONS.ERROR })
        }, []),

        reset: useCallback(() => {
            dispatch({ type: ACTIONS.RESET })
        }, [])
    }

    // Computed values (derived state)
    const computed = {
        isLoading: state.status === CONVERSATION_STATES.LOADING ||
                   state.status === CONVERSATION_STATES.RESPONDING,
        isEnded: state.status === CONVERSATION_STATES.ENDED,
        isDisplaying: state.status === CONVERSATION_STATES.DISPLAYING,
        hasError: state.status === CONVERSATION_STATES.ERROR,
        canShowFeedback: state.status === CONVERSATION_STATES.ENDED &&
                         state.isAnimationComplete &&
                         !state.displayMessage?.proposed_responses?.length,
        conversationId: state.conversation?.agent_message?.conversation_id ||
                        state.conversation?.conversation?.id
    }

    return { state, actions, computed }
}

export default useConversationState
